import { createApi } from '@reduxjs/toolkit/query/react'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'
import { v4 as uuidv4 } from 'uuid'
import { RootState } from '../redux/store'
import { configManager } from '../../config/configManager'
import { ePermission, eRole } from '@logicalcommander/types'

export type GettingStartedStepStatus = 'incomplete' | 'finished' | 'skipped'

export interface IGettingStartedSubStep {
  title: string
  description: string
  target: string
  action: string | string[]
  permissions: ePermission[]
  settingsKey?: string
}

export interface IGettingStartedStep {
  title: string
  description: string
  target: string
  action: string | string[]
  permissions: ePermission[]
  subSteps?: IGettingStartedSubStep[]
}

export interface IGettingStartedTemplate {
  name: string
  keyModules: string
  keyTranslation: string
  pageRoute: string
  roles: eRole[]
  steps: IGettingStartedStep[]
}

export interface IGettingStartedProgressItem {
  keyModules: string
  target: string
  status: GettingStartedStepStatus
}

export interface IGettingStartedProgressResult {
  companyId: string
  identity: string
  progress: IGettingStartedProgressItem[]
}

export type PostGettingStartedProgressBody = IGettingStartedProgressItem

export const gettingStartedApi = createApi({
  reducerPath: 'gettingStartedApi',

  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/getting-started`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),

  endpoints: (builder) => ({
    getTemplate: builder.query<IGettingStartedTemplate[], { keyModules?: string[] } | void>({
      query: (arg) => ({
        url: '/templates',
        method: 'GET',
        params: arg ?? {},
      }),
    }),
    getProgress: builder.query<IGettingStartedProgressResult, void>({
      query: () => ({
        url: '/progress',
        method: 'GET',
      }),
    }),
    postProgress: builder.mutation<IGettingStartedProgressResult, PostGettingStartedProgressBody>({
      query: (body) => ({
        url: '/progress',
        method: 'POST',
        body,
      }),
    }),
    resetProgress: builder.mutation<IGettingStartedProgressResult, void>({
      query: () => ({
        url: '/progress/reset',
        method: 'POST',
      }),
    }),
  }),
})

export const {
  useLazyGetTemplateQuery,
  useLazyGetProgressQuery,
  usePostProgressMutation,
  useResetProgressMutation,
} = gettingStartedApi
